import { Injectable } from '@angular/core';
// @ts-expect-error '@uilab/styling/js/light-js-variables' has no type declarations.
import lightJsVariables from '@uilab/styling/js/light-js-variables';

export interface StylingColorItem {
    readonly cssVariable: string;
    readonly colorValue?: string;
    readonly inverseCssVariable?: string;
    readonly boxShadowCssVariable?: string;
    readonly label: string;
}

export interface StylingTypographyItem {
    readonly cssVariable: string;
    readonly label: string;
    readonly longhandSnippet: string;
    readonly shorthandSnippet: string;
}

export interface StylingDataVisColorItem {
    readonly label: string;
    readonly cssVariable: string;
    readonly colorValue?: string;
}

interface TokenLeaf {
    readonly name?: string;
    readonly value?: string;
    readonly original?: {
        readonly value?: TypographyOriginalValue;
    };
}

interface TypographyOriginalValue {
    readonly fontFamily?: string;
    readonly fontWeight?: string;
    readonly lineHeight?: string;
    readonly fontSize?: string;
}

type TokenTree = Record<string, unknown>;

interface ResolvedToken {
    readonly path: readonly string[];
    readonly cssVariable: string;
    readonly colorValue?: string;
    readonly originalValue?: TypographyOriginalValue;
}

export interface StylingTokenPathsConfig {
    readonly lightPath: readonly string[];
    readonly inversePath?: readonly string[];
    readonly includeBoxShadowVariable?: boolean;
    readonly labelPrefix?: string;
    readonly labelFormatter?: (path: readonly string[]) => string;
}

export interface StylingTypographyPathsConfig {
    readonly path: readonly string[];
    readonly labelPrefix?: string;
    readonly labelFormatter?: (path: readonly string[]) => string;
    readonly tokenPathDepth?: number;
}

const tokenRoot = lightJsVariables as {
    readonly uilab?: {
        readonly sys?: {
            readonly color?: TokenTree;
            readonly 'color-inverse'?: TokenTree;
            readonly 'typography-system'?: TokenTree;
        };
    };
};

function toCssVariableName(tokenName?: string): string | undefined {
    if (!tokenName) {
        return undefined;
    }

    return `--${tokenName}`;
}

function toBoxShadowCssVariableName(
    cssVariableName: string,
): string | undefined {
    const match = cssVariableName.match(/-(\d+dp)$/);

    if (!match) {
        return undefined;
    }

    return `--uilab-sys-elevation-box-shadow-${match[1]}`;
}

function compareBackgroundSteps(a: string, b: string): number {
    const aNum = Number(a);
    const bNum = Number(b);
    const hasANumber = Number.isFinite(aNum);
    const hasBNumber = Number.isFinite(bNum);

    if (hasANumber && hasBNumber) {
        return aNum - bNum;
    }

    return a.localeCompare(b);
}

function getNodeAtPath(root: unknown, path: readonly string[]): unknown {
    let current: unknown = root;

    for (const segment of path) {
        if (!current || typeof current !== 'object') {
            return undefined;
        }

        current = (current as Record<string, unknown>)[segment];
    }

    return current;
}

function collectNamedTokens(
    node: unknown,
    currentPath: readonly string[] = [],
): readonly ResolvedToken[] {
    if (!node || typeof node !== 'object') {
        return [];
    }

    const leafName = (node as TokenLeaf).name;
    if (typeof leafName === 'string') {
        const cssVariable = toCssVariableName(leafName);

        if (!cssVariable) {
            return [];
        }

        return [
            {
                path: currentPath,
                cssVariable,
                colorValue: (node as TokenLeaf).value,
                originalValue: (node as TokenLeaf).original?.value,
            },
        ];
    }

    const results: ResolvedToken[] = [];

    for (const [key, value] of Object.entries(
        node as Record<string, unknown>,
    )) {
        const nested = collectNamedTokens(value, [...currentPath, key]);
        results.push(...nested);
    }

    return results;
}

function formatDefaultLabel(
    path: readonly string[],
    labelPrefix: string,
): string {
    const suffix = path.join(' ').trim();

    if (!suffix) {
        return labelPrefix;
    }

    return `${labelPrefix} ${suffix}`;
}

function toPathKey(path: readonly string[]): string {
    return path.join('.');
}

function buildSectionItems(
    config: StylingTokenPathsConfig,
): readonly StylingColorItem[] {
    const lightNode = getNodeAtPath(tokenRoot.uilab?.sys, config.lightPath);
    const inverseNode = config.inversePath
        ? getNodeAtPath(tokenRoot.uilab?.sys, config.inversePath)
        : undefined;

    const lightTokens = collectNamedTokens(lightNode);
    const inverseTokens = collectNamedTokens(inverseNode);
    const inverseByPath = new Map(
        inverseTokens.map((token) => [
            toPathKey(token.path),
            token.cssVariable,
        ]),
    );

    const items: StylingColorItem[] = [];

    const sortedLightTokens = [...lightTokens].sort((a, b) => {
        const lastA = a.path[a.path.length - 1] ?? '';
        const lastB = b.path[b.path.length - 1] ?? '';

        return compareBackgroundSteps(lastA, lastB);
    });

    for (const token of sortedLightTokens) {
        const inverseCssVariable = inverseByPath.get(toPathKey(token.path));
        const boxShadowCssVariable = config.includeBoxShadowVariable
            ? toBoxShadowCssVariableName(token.cssVariable)
            : undefined;
        const label = config.labelFormatter
            ? config.labelFormatter(token.path)
            : formatDefaultLabel(token.path, config.labelPrefix ?? 'Token');

        items.push({
            cssVariable: token.cssVariable,
            colorValue: token.colorValue,
            inverseCssVariable,
            boxShadowCssVariable,
            label,
        });
    }

    return items;
}

function compareTokenPaths(a: readonly string[], b: readonly string[]): number {
    return a.join('.').localeCompare(b.join('.'), undefined, { numeric: true });
}

function tokenReferenceToCssVariable(reference?: string): string | undefined {
    if (!reference) {
        return undefined;
    }

    const trimmedReference = reference.trim();
    const match = trimmedReference.match(/^\{(.+)\}$/);
    const tokenPath = match ? match[1] : trimmedReference;

    return `--${tokenPath.replace(/\./g, '-')}`;
}

function buildTypographyLonghandSnippet(
    originalValue?: TypographyOriginalValue,
): string {
    if (!originalValue) {
        return '';
    }

    const entries: readonly [string, string | undefined][] = [
        ['font-family', tokenReferenceToCssVariable(originalValue.fontFamily)],
        ['font-size', tokenReferenceToCssVariable(originalValue.fontSize)],
        ['font-weight', tokenReferenceToCssVariable(originalValue.fontWeight)],
        ['line-height', tokenReferenceToCssVariable(originalValue.lineHeight)],
    ];

    return entries
        .filter(([, cssVariable]) => !!cssVariable)
        .map(([property, cssVariable]) => `${property}: var(${cssVariable});`)
        .join('\n');
}

function buildTypographySectionItems(
    config: StylingTypographyPathsConfig,
): readonly StylingTypographyItem[] {
    const node = getNodeAtPath(tokenRoot.uilab?.sys, config.path);
    const tokens = collectNamedTokens(node)
        .filter((token) =>
            config.tokenPathDepth === undefined
                ? true
                : token.path.length === config.tokenPathDepth,
        )
        .sort((a, b) => compareTokenPaths(a.path, b.path));

    return tokens.map((token) => ({
        cssVariable: token.cssVariable,
        label: config.labelFormatter
            ? config.labelFormatter(token.path)
            : formatDefaultLabel(token.path, config.labelPrefix ?? 'Token'),
        longhandSnippet: buildTypographyLonghandSnippet(token.originalValue),
        shorthandSnippet: `font: var(${token.cssVariable});`,
    }));
}

@Injectable({
    providedIn: 'root',
})
export class StylingTokensService {
    getColorTokens(
        config: StylingTokenPathsConfig,
    ): readonly StylingColorItem[] {
        return buildSectionItems(config);
    }

    getTokenGroupKeys(path: readonly string[]): readonly string[] {
        const node = getNodeAtPath(tokenRoot.uilab?.sys, path);

        if (!node || typeof node !== 'object') {
            return [];
        }

        return Object.entries(node as Record<string, unknown>)
            .filter(([, value]) => collectNamedTokens(value).length > 0)
            .map(([key]) => key)
            .sort((a, b) => a.localeCompare(b));
    }

    getTypographyTokens(
        config: StylingTypographyPathsConfig,
    ): readonly StylingTypographyItem[] {
        return buildTypographySectionItems(config);
    }

    getDataVisColorItems(
        tint: string,
        maxItems = 12,
        inverseMode = false,
    ): readonly StylingDataVisColorItem[] {
        const tokenItems = this.getColorTokens({
            lightPath: inverseMode
                ? ['color-inverse', 'data-vis', tint]
                : ['color', 'data-vis', tint],
            labelFormatter: (path) => `${tint} ${path[path.length - 1] ?? ''}`,
        });

        return tokenItems.slice(0, maxItems).map((item, index) => ({
            label: `${tint} ${index + 1}`,
            cssVariable: item.cssVariable,
            colorValue: item.colorValue,
        }));
    }

    buildDataVisCssVariableSnippet(
        items: readonly StylingDataVisColorItem[],
    ): string {
        return items
            .map((item) => `${item.label}: ${item.cssVariable}`)
            .join('\n');
    }
}
